import '.a{display:flex;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.aa{gap:24px;}.ssr-plugin'
import '.ae{justify-content:space-between;}.ssr-plugin'
import '.de{padding:24px;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.k{margin:0 auto;}.ssr-plugin'
import '.a9{cursor:pointer;}.ssr-plugin'
import '.df{max-height:36px;}.ssr-plugin'
import '.dg{height:auto;}.ssr-plugin'
import '.dh{width:auto;}.ssr-plugin'
import '.b8{gap:8px;}.ssr-plugin'
import '.a3{gap:4px;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.bc{align-items:start;}.ssr-plugin'
import '.bo{justify-content:start;}.ssr-plugin'
import '.b2{gap:16px;}.ssr-plugin'
import '.ab{height:1px;}.ssr-plugin'
import '.ac{align-self:stretch;}.ssr-plugin'
import '.di{background:var(--Outlines, rgba(0, 0, 0, 0.10));}.ssr-plugin'
import '.x{border:none;}.ssr-plugin'
import '.ad{margin:0px;}.ssr-plugin'
import { FC, useState, useMemo, useEffect } from 'react'
import { Style, styled } from 'inlines'
import { useDependency } from '../hooks/useDependency'
import { capitalize } from '../utils/capitalize'
import { Button } from './Button'
import { Text } from './Text'
import { useTheme } from '../providers/useTheme'
import { useIsMobile } from '../hooks/useIsMobile'
import { Icon } from './Icon'
import { Link } from 'wouter'
import { useAppCtx } from '../providers/useAppCtx'
import { Language } from '@/apps/cms/components/SiteLanguages'
import useLocalStorage from '@based/use-local-storage'
import { LanguageSelector } from './LanguageSelector'
import { useContentQuery } from '../hooks/useContentQuery'
import { useTranslations } from '../hooks/useTranslations'
import { useNavigation } from '../hooks/useNavigation'

export type NavigationItem = {
  label: string
  route: string
}

export type NavigationProps = {
  as?: 'nav' | 'footer'
  isExpandable?: boolean
  isCartVisible?: boolean
  cartItems?: number
  languages: Language[]
  style?: Style
  'aria-label'?: string
}

type ItemsProps = {
  as: 'nav' | 'footer'
  isMobile: boolean
  items: NavigationItem[]
  colors: ReturnType<typeof useTheme>['colors']
  votesCount?: number
  isCartVisible?: boolean
}

const Items: FC<ItemsProps> = ({
  as,
  isMobile,
  items,
  colors,
  votesCount,
  isCartVisible,
}) => {
  return (
    <div className="a h"
      style={{
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'start' : 'center',
        justifyContent: isMobile ? 'start' : 'center',
        gap: isMobile ? 16 : 24,}}
    >
      {items?.map((item, index) => {
        if (item.route === '/cart' && !isCartVisible) {
          return null
        }

        if (!item.label) {
          return null
        }

        return (
          <Link
            id={`navigation-link-${item.label.toLowerCase()}`}
            key={`${item.label}-${item.route}-${index}`}
            href={item.route}
            style={{ textDecoration: 'none' }}
          >
            <Text
              variant={
                as === 'footer'
                  ? 'subheading-medium'
                  : isMobile
                    ? 'subtitle'
                    : 'subheading-medium'
              }
              color={colors.textPrimary}
            >
              {item.label}{' '}
              {item.route === '/cart' && votesCount
                ? ` (${String(votesCount)})`
                : null}
            </Text>
          </Link>
        )
      })}
    </div>
  )
}

export const Navigation: FC<NavigationProps> = ({
  as = 'nav',
  isExpandable = false,
  isCartVisible = false,
  cartItems = 0,
  'aria-label': ariaLabelProp,
  languages,
  style,
}) => {
  const isMobile = useIsMobile()
  const { colors } = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const { votesCount } = useAppCtx()
  const [storageActiveLanguage, setStorageActiveLanguage] =
    useLocalStorage('language')
  const navigation = useNavigation()
  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev)
  }

  const { data: ed = {} } = useContentQuery('edition')

  const { data: dictionary = {} } = useTranslations('navigation')

  const logo = ed?.htmlConfig?.logo?.src

  const navLabel =
    ariaLabelProp ?? (as === 'footer' ? 'Footer navigation' : 'Main navigation')

  const menuId =
    as === 'footer' ? 'navigation-menu-footer' : 'navigation-menu-nav'

  const showMobileMenu = isMobile && (!isExpandable || isExpanded)

  const defaultLanguage = useMemo(
    () => languages?.filter((language) => Boolean(language.default))[0]?.value,
    [useDependency(languages)],
  )

  useEffect(() => {
    if (!storageActiveLanguage) {
      setStorageActiveLanguage(defaultLanguage)
    }
  }, [defaultLanguage])

  return (
    <styled.nav className="a i aa ae de h"
      {...(as === 'nav' ? { 'aria-label': navLabel } : {})}
      style={{
        borderBottom: as !== 'footer' ? `1px solid ${colors.border}` : 'none',
        backdropFilter: `blur(24px)`,
        background: colors.background,
        '&:before': {
          content: "' '",
          position: 'absolute',
          inset: 0,
          opacity: 0.05,
          background: as === 'footer' ? colors.textPrimary : 'transparent',
          pointerEvents: 'none',
        },
        ...style,
      }}
    >
      <div className="a d ae aa h k"
        style={{
          maxWidth: !isMobile ? 1280 : undefined,}}
      >
        {logo && as === 'nav' ? (
          <Link href="/" style={{ textDecoration: 'none' }}>
            <img className="a9 df dg dh"
              src={logo}
              style={{
                maxWidth: isMobile ? 150 : 192,}}
            />
          </Link>
        ) : (
          <Link href="/" style={{ textDecoration: 'none' }}>
            <Text
              isNotSelectable
              isAlwaysVisible
              variant="display-regular"
              color={colors.textSecondary}
              style={{ fontSize: 16 }}
            >
              {capitalize(dictionary?.contest_title)}
            </Text>
          </Link>
        )}

        {!isMobile && as === 'footer' && (
          <nav className="a ae d"
            aria-label={navLabel}
          >
            <Items
              as={as}
              isMobile={isMobile}
              items={navigation}
              colors={colors}
              votesCount={votesCount}
              isCartVisible={isCartVisible}
            />
          </nav>
        )}

        {!isMobile && as !== 'footer' && (
          <LanguageSelector
            value={storageActiveLanguage}
            defaultValue={defaultLanguage}
            languages={languages}
            style={{ marginLeft: 'auto' }}
          />
        )}

        <div className="a b8 d">
          {isCartVisible && (
            <Link href="/cart">
              <Button
                id="navigation-link-cart"
                isRaw
                color={colors.textSecondary}
                style={{ padding: 6 }}
              >
                <span className="a a3 d c"
                >
                  <Icon variant="bag" color={colors.textSecondary} />
                  <Text
                    isNotSelectable
                    color={colors.textSecondary}
                    style={{ fontSize: 16 }}
                  >
                    {String(cartItems)}
                  </Text>
                </span>
              </Button>
            </Link>
          )}

          {isMobile && isExpandable && (
            <Button
              id="navigation-open-menu"
              isRaw
              on={{ click: handleToggleExpand }}
              aria-label={capitalize(
                (isExpanded
                  ? dictionary?.close_menu_button
                  : dictionary?.open_menu_button) ?? '',
              )}
              aria-expanded={isExpanded}
              aria-controls={menuId}
              style={{ padding: 6 }}
            >
              <span
                style={{
                  transform: isExpanded ? 'rotate(270deg)' : 'rotate(90deg)',
                }}
              >
                <Icon
                  variant="chevron"
                  color={colors.textSecondary}
                  aria-hidden={true}
                />
              </span>
            </Button>
          )}

          {!isMobile && as === 'footer' && (
            <LanguageSelector
              value={storageActiveLanguage}
              defaultValue={defaultLanguage}
              languages={languages}
            />
          )}
        </div>
      </div>

      {showMobileMenu && (
        <div className="a i bc bo b2 h"
          id={menuId}
          {...(as === 'footer' ? { 'aria-label': navLabel } : {})}
        >
          <Items
            as={as}
            isMobile={isMobile}
            items={navigation}
            colors={colors}
            votesCount={votesCount}
            isCartVisible={isCartVisible}
          />

          <hr className="ab ac di x ad"
          />

          <LanguageSelector
            value={storageActiveLanguage}
            defaultValue={defaultLanguage}
            languages={languages}
          />
        </div>
      )}
    </styled.nav>
  )
}
